import type {
  BehaviorConnection,
  BehaviorNode,
  FunctionCallActionNode,
  Port,
} from "./types.js";
import type {
  BehaviorGraph,
  SceneFunctionEntry,
  SceneFunctionPort,
} from "../layout/types.js";
import type { PrimitiveValueType } from "./types.js";
import { centerBehaviorGraphInViewport } from "./behavior-graph-layout.js";

/** Flow port ids on function terminals and call nodes - not removable. */
export const FUNCTION_FLOW_OUT_PORT_ID = "out";
export const FUNCTION_FLOW_IN_PORT_ID = "in";

/** Primitive value types offered when adding a function input or output port. */
export const FUNCTION_PARAMETER_VALUE_TYPES: readonly PrimitiveValueType[] = [
  "number",
  "string",
  "boolean",
  "color",
  "date",
  "fontFamily",
  "fontWeight",
  "textAlign",
  "textTransform",
  "whiteSpace",
  "textDecorationLine",
  "textDecorationColor",
  "textDecorationStyle",
  "textDecorationThickness",
  "gradient",
  "filter",
  "boxShadow",
  "textShadow",
  "border",
  "borderRadius",
  "spacing",
  "list",
  "object",
] as const;

/** All port value types for function signatures, including extra flow branches. */
export const FUNCTION_PORT_VALUE_TYPES: readonly string[] = [
  "flow",
  ...FUNCTION_PARAMETER_VALUE_TYPES,
];

export type FunctionPortValueType =
  | (typeof FUNCTION_PARAMETER_VALUE_TYPES)[number]
  | "flow";

/**
 * Builds output ports for a `functionBegin` node from catalog inputs plus flow `out`.
 */
export function buildFunctionBeginPorts(
  inputs: readonly SceneFunctionPort[],
  flowOutLabel: string,
): Port[] {
  return [
    {
      id: FUNCTION_FLOW_OUT_PORT_ID,
      label: flowOutLabel,
      direction: "output",
      valueType: "flow",
    },
    ...inputs.map((entry) => ({
      id: entry.id,
      label: entry.label,
      direction: "output" as const,
      valueType: entry.valueType,
    })),
  ];
}

/**
 * Builds input ports for a `functionEnd` node from catalog outputs plus flow `in`.
 */
export function buildFunctionEndPorts(
  outputs: readonly SceneFunctionPort[],
  flowInLabel: string,
): Port[] {
  return [
    {
      id: FUNCTION_FLOW_IN_PORT_ID,
      label: flowInLabel,
      direction: "input",
      valueType: "flow",
    },
    ...outputs.map((entry) => ({
      id: entry.id,
      label: entry.label,
      direction: "input" as const,
      valueType: entry.valueType,
    })),
  ];
}

/**
 * Builds ports for a `call` action from a function signature.
 * Preserves literal values on input ports when `existingPorts` is provided.
 */
export function buildFunctionCallPorts(
  entry: Pick<SceneFunctionEntry, "inputs" | "outputs">,
  labels: { flowIn: string; flowOut: string },
  existingPorts?: readonly Port[],
): Port[] {
  const preservedValue = (portId: string) =>
    existingPorts?.find((port) => port.id === portId)?.value;

  return [
    {
      id: FUNCTION_FLOW_IN_PORT_ID,
      label: labels.flowIn,
      direction: "input",
      valueType: "flow",
    },
    ...entry.inputs.map((input) => ({
      id: input.id,
      label: input.label,
      direction: "input" as const,
      valueType: input.valueType,
      value: preservedValue(input.id),
    })),
    {
      id: FUNCTION_FLOW_OUT_PORT_ID,
      label: labels.flowOut,
      direction: "output",
      valueType: "flow",
    },
    ...entry.outputs.map((output) => ({
      id: output.id,
      label: output.label,
      direction: "output" as const,
      valueType: output.valueType,
    })),
  ];
}

/**
 * Synchronizes `functionBegin` / `functionEnd` terminal ports with catalog I/O.
 * Drops connections that reference removed value ports.
 */
export function syncFunctionGraphTerminalPorts(
  graph: BehaviorGraph,
  entry: Pick<SceneFunctionEntry, "inputs" | "outputs">,
  labels: { flowIn: string; flowOut: string },
): BehaviorGraph {
  const nodes = graph.nodes.map((node) => {
    if (node.type === "control" && node.controlType === "functionBegin") {
      return {
        ...node,
        ports: buildFunctionBeginPorts(entry.inputs, labels.flowOut),
      };
    }
    if (node.type === "control" && node.controlType === "functionEnd") {
      return {
        ...node,
        ports: buildFunctionEndPorts(entry.outputs, labels.flowIn),
      };
    }
    return node;
  });

  const portIdsByNodeId = new Map(
    nodes.map((node) => [node.id, new Set(node.ports.map((port) => port.id))]),
  );

  const connections = graph.connections.filter((connection) => {
    const fromPorts = portIdsByNodeId.get(connection.fromNodeId);
    const toPorts = portIdsByNodeId.get(connection.toNodeId);
    return (
      Boolean(fromPorts?.has(connection.fromPortId)) &&
      Boolean(toPorts?.has(connection.toPortId))
    );
  });

  return { nodes, connections };
}

/**
 * Rebuilds `call` node ports from the function catalog and optionally drops
 * connections that reference removed ports.
 */
export function syncCallNodePortsInGraph(
  graph: Pick<BehaviorGraph, "nodes" | "connections">,
  functions: readonly Pick<SceneFunctionEntry, "id" | "inputs" | "outputs">[],
  labels: { flowIn: string; flowOut: string },
  options?: { filterConnections?: boolean },
): BehaviorGraph {
  const nodes = graph.nodes.map((node) => {
    if (node.type !== "action" || node.kind !== "call" || !node.functionId) {
      return node;
    }
    const entry = functions.find((item) => item.id === node.functionId);
    if (!entry) return node;
    return {
      ...node,
      ports: buildFunctionCallPorts(entry, labels, node.ports),
    };
  });

  if (options?.filterConnections === false) {
    return { nodes, connections: graph.connections };
  }

  const portIdsByNodeId = new Map(
    nodes.map((node) => [node.id, new Set(node.ports.map((port) => port.id))]),
  );

  const connections = graph.connections.filter((connection) => {
    const fromPorts = portIdsByNodeId.get(connection.fromNodeId);
    const toPorts = portIdsByNodeId.get(connection.toNodeId);
    return (
      Boolean(fromPorts?.has(connection.fromPortId)) &&
      Boolean(toPorts?.has(connection.toPortId))
    );
  });

  return { nodes, connections };
}

/**
 * Returns whether a port id is a protected flow terminal on begin/end nodes.
 */
export function isProtectedFunctionTerminalPortId(portId: string): boolean {
  return (
    portId === FUNCTION_FLOW_IN_PORT_ID || portId === FUNCTION_FLOW_OUT_PORT_ID
  );
}

/**
 * Reads value output ports from the `functionEnd` node when catalog `outputs` is empty.
 */
export function getFunctionOutputPortsFromGraph(
  graph: Pick<BehaviorGraph, "nodes">,
): SceneFunctionPort[] {
  const endNode = graph.nodes.find(
    (node) => node.type === "control" && node.controlType === "functionEnd",
  );
  if (!endNode) {
    return [];
  }

  return endNode.ports
    .filter(
      (port) =>
        port.direction === "input" &&
        !isProtectedFunctionTerminalPortId(port.id) &&
        port.valueType !== "flow",
    )
    .map((port) => ({
      id: port.id,
      label: port.label ?? port.id,
      valueType: port.valueType,
    }));
}

/**
 * Resolves function output ports from the catalog entry or from the graph terminals.
 */
export function resolveFunctionOutputPorts(
  entry: Pick<SceneFunctionEntry, "outputs" | "graph">,
): SceneFunctionPort[] {
  if (entry.outputs.length > 0) {
    return [...entry.outputs];
  }
  return getFunctionOutputPortsFromGraph(entry.graph);
}

/** Maximum nested `call` depth before invocation is refused (stack overflow guard). */
export const MAX_FUNCTION_CALL_DEPTH = 32;

/** One active frame while draining a scene function body. */
export interface FunctionCallFrame {
  functionId: string;
  callNodeId: string;
}

/**
 * Returns whether a `call` to `functionId` must be refused given the active stack.
 */
export function isFunctionCallBlocked(
  callStack: readonly FunctionCallFrame[] | undefined,
  functionId: string,
): boolean {
  const stack = callStack ?? [];
  if (stack.some((frame) => frame.functionId === functionId)) {
    return true;
  }
  return stack.length >= MAX_FUNCTION_CALL_DEPTH;
}

function getOutgoingNodeIdsByPort(
  connections: BehaviorGraph["connections"],
  fromNodeId: string,
  fromPortId: string,
): string[] {
  return connections
    .filter(
      (connection) =>
        connection.fromNodeId === fromNodeId &&
        connection.fromPortId === fromPortId,
    )
    .map((connection) => connection.toNodeId);
}

/**
 * Returns flow output port ids that may emit when flow enters this node.
 */
export function getBehaviorFlowOutputPortIds(node: BehaviorNode): string[] {
  if (node.type === "control") {
    switch (node.controlType) {
      case "functionBegin":
      case "mergeFlows":
      case "wait":
        return ["out"];
      case "ifElse":
        return ["true", "false"];
      case "repeat":
        return ["body", "done"];
      case "functionEnd":
      case "eventOutput":
        return [];
      default:
        return [];
    }
  }
  if (node.type === "event") return ["out"];
  if (node.type === "action") {
    if (node.kind === "animEnd") return [];
    return ["out"];
  }
  return [];
}

/**
 * Returns whether a function graph already contains a `functionBegin` node.
 */
export function functionGraphHasFunctionBegin(
  graph: Pick<BehaviorGraph, "nodes"> | undefined,
): boolean {
  if (!graph) return false;
  return graph.nodes.some(
    (node) => node.type === "control" && node.controlType === "functionBegin",
  );
}

/**
 * Returns whether a function graph already contains a `functionEnd` node.
 */
export function functionGraphHasFunctionEnd(
  graph: Pick<BehaviorGraph, "nodes"> | undefined,
): boolean {
  if (!graph) return false;
  return graph.nodes.some(
    (node) => node.type === "control" && node.controlType === "functionEnd",
  );
}

/**
 * A scene function is publishable when its graph contains exactly one
 * `functionBegin` and one `functionEnd`, with a flow path from begin `out`
 * to end `in`.
 */
export function isFunctionGraphPublishable(
  graph: Pick<BehaviorGraph, "nodes" | "connections"> | undefined,
): boolean {
  if (!graph) return false;

  const beginNodes = graph.nodes.filter(
    (node) => node.type === "control" && node.controlType === "functionBegin",
  );
  const endNodes = graph.nodes.filter(
    (node) => node.type === "control" && node.controlType === "functionEnd",
  );
  if (beginNodes.length !== 1 || endNodes.length !== 1) return false;

  const begin = beginNodes[0]!;
  const end = endNodes[0]!;

  const endHasFlowIn = graph.connections.some(
    (connection) =>
      connection.toNodeId === end.id && connection.toPortId === "in",
  );
  if (!endHasFlowIn) return false;

  const visited = new Set<string>();
  const queue = getOutgoingNodeIdsByPort(graph.connections, begin.id, "out");

  while (queue.length > 0) {
    const nodeId = queue.shift()!;
    if (nodeId === end.id) return true;
    if (visited.has(nodeId)) continue;
    visited.add(nodeId);

    const node = graph.nodes.find((entry) => entry.id === nodeId);
    if (!node) continue;

    for (const portId of getBehaviorFlowOutputPortIds(node)) {
      for (const nextId of getOutgoingNodeIdsByPort(
        graph.connections,
        nodeId,
        portId,
      )) {
        if (!visited.has(nextId)) queue.push(nextId);
      }
    }
  }

  return false;
}

/**
 * Builds the default graph for a newly created scene function:
 * `functionBegin` on the left and `functionEnd` on the right, vertically centered.
 */
export function createDefaultFunctionGraph(options: {
  canvasWidth: number;
  canvasHeight: number;
  beginNodeId?: string;
  endNodeId?: string;
  flowOutPortLabel?: string;
  flowInPortLabel?: string;
}): BehaviorGraph {
  const canvasWidth = Math.max(1, options.canvasWidth);
  const canvasHeight = Math.max(1, options.canvasHeight);
  const beginNodeId = options.beginNodeId ?? crypto.randomUUID();
  const endNodeId = options.endNodeId ?? crypto.randomUUID();
  const y = Math.max(0, Math.round(canvasHeight / 2 - 50));
  const beginX = 40;
  const endX = Math.max(beginX + 240, canvasWidth - 320);

  const functionBeginNode: BehaviorNode = {
    id: beginNodeId,
    x: beginX,
    y,
    type: "control",
    controlType: "functionBegin",
    dataValueType: "flow",
    ports: buildFunctionBeginPorts([], options.flowOutPortLabel ?? ""),
  };

  const functionEndNode: BehaviorNode = {
    id: endNodeId,
    x: endX,
    y,
    type: "control",
    controlType: "functionEnd",
    dataValueType: "flow",
    ports: buildFunctionEndPorts([], options.flowInPortLabel ?? ""),
  };

  return {
    nodes: [functionBeginNode, functionEndNode],
    connections: [],
  };
}

// ── Extract selection to scene function ─────────────────────────────────────

/** Flow entry boundary of a behavior subgraph selection. */
export interface SelectionFunctionFlowEntry {
  nodeId: string;
  /** Flow input port, or `undefined` when flow starts inside the node (e.g. event). */
  portId?: string;
}

/** Flow exit boundary of a behavior subgraph selection. */
export interface SelectionFunctionFlowExit {
  nodeId: string;
  portId: string;
}

function isFlowPort(port: Port, direction: Port["direction"]): boolean {
  return port.direction === direction && port.valueType === "flow";
}

function toFunctionParameterValueType(
  valueType: string,
): FunctionPortValueType | null {
  if (valueType === "flow" || valueType === "binding") {
    return null;
  }
  if (
    FUNCTION_PARAMETER_VALUE_TYPES.includes(valueType as PrimitiveValueType)
  ) {
    return valueType as FunctionPortValueType;
  }
  return null;
}

/**
 * Flow input ports on selected nodes that are not fed from another selected node.
 */
export function findSelectionExternalFlowInputs(
  graph: Pick<BehaviorGraph, "nodes" | "connections">,
  selectedNodeIds: readonly string[],
): SelectionFunctionFlowEntry[] {
  const selectedIds = new Set(selectedNodeIds);
  const nodesById = new Map(graph.nodes.map((node) => [node.id, node]));
  const entries: SelectionFunctionFlowEntry[] = [];

  for (const nodeId of selectedIds) {
    const node = nodesById.get(nodeId);
    if (!node) continue;

    const flowInputs = node.ports.filter((port) => isFlowPort(port, "input"));
    for (const port of flowInputs) {
      const fedFromSelection = graph.connections.some(
        (connection) =>
          connection.toNodeId === nodeId &&
          connection.toPortId === port.id &&
          selectedIds.has(connection.fromNodeId),
      );
      if (!fedFromSelection) {
        entries.push({ nodeId, portId: port.id });
      }
    }

    if (flowInputs.length > 0) continue;

    const fedBySelectedFlow = graph.connections.some((connection) => {
      if (
        connection.toNodeId !== nodeId ||
        !selectedIds.has(connection.fromNodeId)
      ) {
        return false;
      }
      const toPort = node.ports.find(
        (entry) => entry.id === connection.toPortId,
      );
      return toPort != null && isFlowPort(toPort, "input");
    });
    if (!fedBySelectedFlow && getBehaviorFlowOutputPortIds(node).length > 0) {
      entries.push({ nodeId });
    }
  }

  return entries;
}

/**
 * Flow output ports on selected nodes that are not wired to another selected node.
 */
export function findSelectionExternalFlowOutputs(
  graph: Pick<BehaviorGraph, "nodes" | "connections">,
  selectedNodeIds: readonly string[],
): SelectionFunctionFlowExit[] {
  const selectedIds = new Set(selectedNodeIds);
  const nodesById = new Map(graph.nodes.map((node) => [node.id, node]));
  const exits: SelectionFunctionFlowExit[] = [];

  for (const nodeId of selectedIds) {
    const node = nodesById.get(nodeId);
    if (!node) continue;

    for (const port of node.ports) {
      if (!isFlowPort(port, "output")) continue;
      const wiredInsideSelection = graph.connections.some(
        (connection) =>
          connection.fromNodeId === nodeId &&
          connection.fromPortId === port.id &&
          selectedIds.has(connection.toNodeId),
      );
      if (!wiredInsideSelection) {
        exits.push({ nodeId, portId: port.id });
      }
    }
  }

  return exits;
}

function getNodesReachableByFlowWithinSelection(
  graph: Pick<BehaviorGraph, "nodes" | "connections">,
  selectedIds: Set<string>,
  startNodeId: string,
): Set<string> {
  const nodesById = new Map(graph.nodes.map((node) => [node.id, node]));
  const visited = new Set<string>();
  const queue = [startNodeId];

  while (queue.length > 0) {
    const nodeId = queue.shift()!;
    if (visited.has(nodeId)) continue;
    visited.add(nodeId);

    const node = nodesById.get(nodeId);
    if (!node) continue;

    for (const portId of getBehaviorFlowOutputPortIds(node)) {
      for (const nextId of getOutgoingNodeIdsByPort(
        graph.connections,
        nodeId,
        portId,
      )) {
        if (selectedIds.has(nextId) && !visited.has(nextId)) {
          queue.push(nextId);
        }
      }
    }
  }

  return visited;
}

function nodeParticipatesInFlow(node: BehaviorNode): boolean {
  return node.ports.some((port) => port.valueType === "flow");
}

function isValueConnection(
  connection: BehaviorConnection,
  nodesById: Map<string, BehaviorNode>,
): boolean {
  const fromNode = nodesById.get(connection.fromNodeId);
  const toNode = nodesById.get(connection.toNodeId);
  if (!fromNode || !toNode) return false;

  const fromPort = fromNode.ports.find(
    (port) => port.id === connection.fromPortId,
  );
  const toPort = toNode.ports.find((port) => port.id === connection.toPortId);
  if (!fromPort || !toPort) return false;

  return fromPort.valueType !== "flow" && toPort.valueType !== "flow";
}

/**
 * Flow-reachable nodes plus data-only nodes wired (directly or transitively)
 * to that flow core within the selection.
 */
function getSelectionAllowedNodes(
  graph: Pick<BehaviorGraph, "nodes" | "connections">,
  selectedIds: Set<string>,
  entryNodeId: string,
): Set<string> | null {
  const nodesById = new Map(graph.nodes.map((node) => [node.id, node]));
  const flowReachable = getNodesReachableByFlowWithinSelection(
    graph,
    selectedIds,
    entryNodeId,
  );

  for (const nodeId of selectedIds) {
    const node = nodesById.get(nodeId);
    if (!node) return null;
    if (nodeParticipatesInFlow(node) && !flowReachable.has(nodeId)) {
      return null;
    }
  }

  const allowed = new Set(flowReachable);
  let expanded = true;
  while (expanded) {
    expanded = false;
    for (const nodeId of selectedIds) {
      if (allowed.has(nodeId)) continue;

      const attachedToFlowCore = graph.connections.some((connection) => {
        if (
          !selectedIds.has(connection.fromNodeId) ||
          !selectedIds.has(connection.toNodeId) ||
          !isValueConnection(connection, nodesById)
        ) {
          return false;
        }
        if (connection.fromNodeId === nodeId) {
          return allowed.has(connection.toNodeId);
        }
        if (connection.toNodeId === nodeId) {
          return allowed.has(connection.fromNodeId);
        }
        return false;
      });

      if (attachedToFlowCore) {
        allowed.add(nodeId);
        expanded = true;
      }
    }
  }

  if (allowed.size !== selectedIds.size) {
    return null;
  }

  return allowed;
}

function selectionHasFlowPath(
  graph: Pick<BehaviorGraph, "nodes" | "connections">,
  selectedIds: Set<string>,
  entry: SelectionFunctionFlowEntry,
  exit: SelectionFunctionFlowExit,
): boolean {
  const allowed = getSelectionAllowedNodes(graph, selectedIds, entry.nodeId);
  if (!allowed) return false;
  return allowed.has(exit.nodeId);
}

/**
 * Returns whether a selection can be promoted to a scene function:
 * at least two nodes, no event nodes, exactly one external flow entry and one
 * external flow exit, a flow path from entry to exit, and any extra selected
 * nodes are data-only nodes attached to that flow core.
 */
export function analyzeSelectionForFunctionExtraction(
  graph: Pick<BehaviorGraph, "nodes" | "connections">,
  selectedNodeIds: readonly string[],
): {
  entry: SelectionFunctionFlowEntry;
  exit: SelectionFunctionFlowExit;
} | null {
  if (selectedNodeIds.length < 2) return null;

  const selectedIds = new Set(selectedNodeIds);
  const nodesById = new Map(graph.nodes.map((node) => [node.id, node]));

  for (const nodeId of selectedIds) {
    const node = nodesById.get(nodeId);
    if (!node) return null;
    if (node.type === "event") return null;
  }

  const entries = findSelectionExternalFlowInputs(graph, selectedNodeIds);
  const exits = findSelectionExternalFlowOutputs(graph, selectedNodeIds);
  if (entries.length !== 1 || exits.length !== 1) return null;

  const entry = entries[0]!;
  const exit = exits[0]!;

  if (!selectionHasFlowPath(graph, selectedIds, entry, exit)) {
    return null;
  }

  return { entry, exit };
}

export interface FunctionExtractionFromSelectionOptions {
  functionId: string;
  functionName: string;
  flowInLabel: string;
  flowOutLabel: string;
  /** Visible behavior canvas width in world coordinates (zoom = 1). */
  canvasWidth: number;
  /** Visible behavior canvas height in world coordinates (zoom = 1). */
  canvasHeight: number;
}

export interface FunctionExtractionFromSelectionResult {
  catalogEntry: SceneFunctionEntry;
  parentGraph: BehaviorGraph;
}

/**
 * Promotes a behavior subgraph selection into a new scene function catalog entry
 * and replaces the selection with a `call` action in the parent graph.
 */
export function extractBehaviorSelectionToSceneFunction(
  parentGraph: BehaviorGraph,
  selectedNodeIds: readonly string[],
  options: FunctionExtractionFromSelectionOptions,
): FunctionExtractionFromSelectionResult | null {
  const boundaries = analyzeSelectionForFunctionExtraction(
    parentGraph,
    selectedNodeIds,
  );
  if (!boundaries) return null;

  const { entry, exit } = boundaries;
  const selectedIds = new Set(selectedNodeIds);
  const nodesById = new Map(parentGraph.nodes.map((node) => [node.id, node]));

  const externalValueInputsByTargetKey = new Map<
    string,
    {
      targetNodeId: string;
      targetPortId: string;
      valueType: FunctionPortValueType;
      label: string;
    }
  >();

  const externalValueOutputsBySourceKey = new Map<
    string,
    {
      sourceNodeId: string;
      sourcePortId: string;
      valueType: FunctionPortValueType;
      label: string;
    }
  >();

  for (const connection of parentGraph.connections) {
    if (selectedIds.has(connection.fromNodeId)) {
      if (!selectedIds.has(connection.toNodeId)) {
        const fromNode = nodesById.get(connection.fromNodeId);
        const fromPort = fromNode?.ports.find(
          (port) => port.id === connection.fromPortId,
        );
        if (
          fromPort &&
          fromPort.direction === "output" &&
          fromPort.valueType !== "flow"
        ) {
          const valueType = toFunctionParameterValueType(fromPort.valueType);
          if (valueType) {
            const key = `${connection.fromNodeId}:${connection.fromPortId}`;
            if (!externalValueOutputsBySourceKey.has(key)) {
              externalValueOutputsBySourceKey.set(key, {
                sourceNodeId: connection.fromNodeId,
                sourcePortId: connection.fromPortId,
                valueType,
                label: fromPort.label,
              });
            }
          }
        }
      }
      continue;
    }

    if (!selectedIds.has(connection.toNodeId)) continue;

    const toNode = nodesById.get(connection.toNodeId);
    const toPort = toNode?.ports.find(
      (port) => port.id === connection.toPortId,
    );
    if (
      !toPort ||
      toPort.direction !== "input" ||
      toPort.valueType === "flow"
    ) {
      continue;
    }

    const valueType = toFunctionParameterValueType(toPort.valueType);
    if (!valueType) continue;

    const key = `${connection.toNodeId}:${connection.toPortId}`;
    if (!externalValueInputsByTargetKey.has(key)) {
      externalValueInputsByTargetKey.set(key, {
        targetNodeId: connection.toNodeId,
        targetPortId: connection.toPortId,
        valueType,
        label: toPort.label,
      });
    }
  }

  const inputs: SceneFunctionPort[] = [];
  const outputs: SceneFunctionPort[] = [];
  const catalogInputIdByTargetKey = new Map<string, string>();
  const catalogOutputIdBySourceKey = new Map<string, string>();

  for (const [key, external] of externalValueInputsByTargetKey) {
    const portId = crypto.randomUUID();
    catalogInputIdByTargetKey.set(key, portId);
    inputs.push({
      id: portId,
      label: external.label,
      valueType: external.valueType,
    });
  }

  for (const [key, external] of externalValueOutputsBySourceKey) {
    const portId = crypto.randomUUID();
    catalogOutputIdBySourceKey.set(key, portId);
    outputs.push({
      id: portId,
      label: external.label,
      valueType: external.valueType,
    });
  }

  const idMap = new Map<string, string>();
  for (const nodeId of selectedIds) {
    idMap.set(nodeId, crypto.randomUUID());
  }

  const selectedNodes = parentGraph.nodes.filter((node) =>
    selectedIds.has(node.id),
  );
  const minX = Math.min(...selectedNodes.map((node) => node.x));
  const minY = Math.min(...selectedNodes.map((node) => node.y));
  const maxX = Math.max(...selectedNodes.map((node) => node.x));

  const beginNodeId = crypto.randomUUID();
  const endNodeId = crypto.randomUUID();
  const beginX = 40;
  const bodyOffsetX = beginX + 220 - minX;
  const bodyOffsetY = Math.max(0, Math.round(200 - minY));
  const endX = Math.max(beginX + 280, maxX + bodyOffsetX + 120);

  const clonedNodes: BehaviorNode[] = selectedNodes.map((node) => ({
    ...structuredClone(node),
    id: idMap.get(node.id) ?? node.id,
    x: node.x + bodyOffsetX,
    y: node.y + bodyOffsetY,
    ports: node.ports.map((port) => ({ ...port })),
  }));

  const bodyConnections: BehaviorConnection[] = parentGraph.connections
    .filter(
      (connection) =>
        selectedIds.has(connection.fromNodeId) &&
        selectedIds.has(connection.toNodeId),
    )
    .map((connection) => ({
      id: crypto.randomUUID(),
      fromNodeId: idMap.get(connection.fromNodeId) ?? connection.fromNodeId,
      fromPortId: connection.fromPortId,
      toNodeId: idMap.get(connection.toNodeId) ?? connection.toNodeId,
      toPortId: connection.toPortId,
    }));

  const anchorY = Math.min(...clonedNodes.map((node) => node.y));

  const functionBeginNode: BehaviorNode = {
    id: beginNodeId,
    x: beginX,
    y: anchorY,
    type: "control",
    controlType: "functionBegin",
    dataValueType: "flow",
    ports: buildFunctionBeginPorts(inputs, options.flowOutLabel),
  };

  const functionEndNode: BehaviorNode = {
    id: endNodeId,
    x: endX,
    y: anchorY,
    type: "control",
    controlType: "functionEnd",
    dataValueType: "flow",
    ports: buildFunctionEndPorts(outputs, options.flowInLabel),
  };

  const remappedEntryNodeId = idMap.get(entry.nodeId);
  const remappedExitNodeId = idMap.get(exit.nodeId);

  if (remappedEntryNodeId) {
    if (entry.portId) {
      bodyConnections.push({
        id: crypto.randomUUID(),
        fromNodeId: beginNodeId,
        fromPortId: FUNCTION_FLOW_OUT_PORT_ID,
        toNodeId: remappedEntryNodeId,
        toPortId: entry.portId,
      });
    } else {
      const entryNode = nodesById.get(entry.nodeId);
      const flowOutPortIds = new Set(
        (entryNode?.ports ?? [])
          .filter((port) => isFlowPort(port, "output"))
          .map((port) => port.id),
      );
      const replacedTargets: Array<{ toNodeId: string; toPortId: string }> = [];

      for (let index = bodyConnections.length - 1; index >= 0; index -= 1) {
        const connection = bodyConnections[index]!;
        if (
          connection.fromNodeId !== remappedEntryNodeId ||
          !flowOutPortIds.has(connection.fromPortId)
        ) {
          continue;
        }
        replacedTargets.push({
          toNodeId: connection.toNodeId,
          toPortId: connection.toPortId,
        });
        bodyConnections.splice(index, 1);
      }

      for (const target of replacedTargets) {
        bodyConnections.push({
          id: crypto.randomUUID(),
          fromNodeId: beginNodeId,
          fromPortId: FUNCTION_FLOW_OUT_PORT_ID,
          toNodeId: target.toNodeId,
          toPortId: target.toPortId,
        });
      }
    }
  }

  if (remappedExitNodeId) {
    bodyConnections.push({
      id: crypto.randomUUID(),
      fromNodeId: remappedExitNodeId,
      fromPortId: exit.portId,
      toNodeId: endNodeId,
      toPortId: FUNCTION_FLOW_IN_PORT_ID,
    });
  }

  for (const [key, external] of externalValueInputsByTargetKey) {
    const inputPortId = catalogInputIdByTargetKey.get(key);
    const targetNodeId = idMap.get(external.targetNodeId);
    if (!inputPortId || !targetNodeId) continue;
    bodyConnections.push({
      id: crypto.randomUUID(),
      fromNodeId: beginNodeId,
      fromPortId: inputPortId,
      toNodeId: targetNodeId,
      toPortId: external.targetPortId,
    });
  }

  for (const [key, external] of externalValueOutputsBySourceKey) {
    const outputPortId = catalogOutputIdBySourceKey.get(key);
    const sourceNodeId = idMap.get(external.sourceNodeId);
    if (!outputPortId || !sourceNodeId) continue;
    bodyConnections.push({
      id: crypto.randomUUID(),
      fromNodeId: sourceNodeId,
      fromPortId: external.sourcePortId,
      toNodeId: endNodeId,
      toPortId: outputPortId,
    });
  }

  const functionGraph: BehaviorGraph = {
    nodes: [functionBeginNode, ...clonedNodes, functionEndNode],
    connections: bodyConnections,
  };

  const syncedFunctionGraph = syncFunctionGraphTerminalPorts(
    functionGraph,
    { inputs, outputs },
    {
      flowIn: options.flowInLabel,
      flowOut: options.flowOutLabel,
    },
  );

  const centeredFunctionGraph = centerBehaviorGraphInViewport(
    syncedFunctionGraph,
    options.canvasWidth,
    options.canvasHeight,
  );

  const catalogEntry: SceneFunctionEntry = {
    id: options.functionId,
    name: options.functionName,
    inputs,
    outputs,
    graph: centeredFunctionGraph,
  };

  const centerX =
    selectedNodes.reduce((sum, node) => sum + node.x, 0) / selectedNodes.length;
  const centerY =
    selectedNodes.reduce((sum, node) => sum + node.y, 0) / selectedNodes.length;

  const callNode: FunctionCallActionNode = {
    id: crypto.randomUUID(),
    x: centerX,
    y: centerY,
    type: "action",
    kind: "call",
    functionId: options.functionId,
    ports: buildFunctionCallPorts(catalogEntry, {
      flowIn: options.flowInLabel,
      flowOut: options.flowOutLabel,
    }),
  };

  const remainingNodes = parentGraph.nodes.filter(
    (node) => !selectedIds.has(node.id),
  );
  const remainingConnections = parentGraph.connections.filter(
    (connection) =>
      !selectedIds.has(connection.fromNodeId) &&
      !selectedIds.has(connection.toNodeId),
  );

  const rewiredConnections: BehaviorConnection[] = [...remainingConnections];

  for (const connection of parentGraph.connections) {
    if (selectedIds.has(connection.fromNodeId)) continue;
    if (!selectedIds.has(connection.toNodeId)) continue;

    const toPort = nodesById
      .get(connection.toNodeId)
      ?.ports.find((port) => port.id === connection.toPortId);

    if (toPort && isFlowPort(toPort, "input")) {
      if (
        connection.toNodeId === entry.nodeId &&
        (!entry.portId || connection.toPortId === entry.portId)
      ) {
        rewiredConnections.push({
          id: crypto.randomUUID(),
          fromNodeId: connection.fromNodeId,
          fromPortId: connection.fromPortId,
          toNodeId: callNode.id,
          toPortId: FUNCTION_FLOW_IN_PORT_ID,
        });
      }
      continue;
    }

    const inputPortId = catalogInputIdByTargetKey.get(
      `${connection.toNodeId}:${connection.toPortId}`,
    );
    if (!inputPortId) continue;

    rewiredConnections.push({
      id: crypto.randomUUID(),
      fromNodeId: connection.fromNodeId,
      fromPortId: connection.fromPortId,
      toNodeId: callNode.id,
      toPortId: inputPortId,
    });
  }

  for (const connection of parentGraph.connections) {
    if (!selectedIds.has(connection.fromNodeId)) continue;
    if (selectedIds.has(connection.toNodeId)) continue;

    const fromPort = nodesById
      .get(connection.fromNodeId)
      ?.ports.find((port) => port.id === connection.fromPortId);

    if (fromPort && isFlowPort(fromPort, "output")) {
      if (
        connection.fromNodeId === exit.nodeId &&
        connection.fromPortId === exit.portId
      ) {
        rewiredConnections.push({
          id: crypto.randomUUID(),
          fromNodeId: callNode.id,
          fromPortId: FUNCTION_FLOW_OUT_PORT_ID,
          toNodeId: connection.toNodeId,
          toPortId: connection.toPortId,
        });
      }
      continue;
    }

    const outputPortId = catalogOutputIdBySourceKey.get(
      `${connection.fromNodeId}:${connection.fromPortId}`,
    );
    if (!outputPortId) continue;

    rewiredConnections.push({
      id: crypto.randomUUID(),
      fromNodeId: callNode.id,
      fromPortId: outputPortId,
      toNodeId: connection.toNodeId,
      toPortId: connection.toPortId,
    });
  }

  return {
    catalogEntry,
    parentGraph: {
      nodes: [...remainingNodes, callNode],
      connections: rewiredConnections,
    },
  };
}
